import { inject, Injectable } from "@angular/core";
import { catchError, EMPTY, firstValueFrom, map, Observable, of, shareReplay, take, throwError } from "rxjs";
import { Page } from "../../../@common/entities/page.interface";
import { Logger } from "../../../@common/log/logger";
import {
    Author,
    AuthorInfo,
    AuthorInfoSort,
    BodyPublishAuthorForm,
    ManageAuthorBody,
    SortOrder,
    V2AuthorsResource,
    VideoInfoSort,
} from "../../../@generated";
import { NavigationService } from "../../navigation/application/navigation.service";

@Injectable({
    providedIn: "root",
})
export class AuthorService {
    private logger = inject(Logger);
    private authorsResource = inject(V2AuthorsResource);
    private navigationService = inject(NavigationService);

    public getAllAuthorInfos(): Observable<AuthorInfo[]> {
        return this.authorsResource
            .getAuthorsInfos(1, 999999, SortOrder.Ascending, AuthorInfoSort.Name, undefined)
            .pipe(
                map((response) => response.results),
                catchError((e) => {
                    this.logger.apiError("Get all author infos failed", e);
                    return of([]);
                })
            );
    }

    public getAuthorInfos(
        page: number,
        pageSize: number,
        order: SortOrder | undefined,
        sort: AuthorInfoSort | undefined,
        query: string | undefined
    ): Observable<Page<AuthorInfo>> {
        return this.authorsResource.getAuthorsInfos(page, pageSize, order, sort, query).pipe(
            catchError((e) => {
                this.logger.apiError("Get all author infos failed", e);
                return of({ count: 0, results: [] });
            })
        );
    }

    public getAuthorBySlug(slug: string): Observable<Author | undefined> {
        return this.authorsResource.getAuthorBySlug(slug).pipe(
            shareReplay(1),
            catchError((e) => {
                if (e.status === 404) {
                    this.navigationService.navigateToNotFound();
                } else {
                    this.logger.apiError("Get author by slug failed", e);
                }

                return EMPTY;
            })
        );
    }

    public getAuthorById(id: number): Observable<Author> {
        return this.authorsResource.getAuthorById(id).pipe(
            shareReplay(1),
            catchError((e) => {
                if (e.status === 404) {
                    this.navigationService.navigateToNotFound();
                } else {
                    this.logger.apiError("Get author by id failed", e);
                }

                return EMPTY;
            })
        );
    }

    public getFollowedAuthors(page: number, pageSize: number): Observable<Page<AuthorInfo>> {
        return this.authorsResource.getFollowedAuthors(page, pageSize).pipe(
            shareReplay(1),
            catchError((e) => {
                this.logger.apiError("Get followed authors failed", e);
                return throwError(() => e);
            })
        );
    }

    public followAuthor(id: number): void {
        this.authorsResource
            .followAuthor(id)
            .pipe(
                take(1),
                catchError((e) => {
                    this.logger.apiError("Follow author failed", e);
                    return throwError(() => e);
                })
            )
            .subscribe();
    }

    public unfollowAuthor(id: number): void {
        this.authorsResource
            .unfollowAuthor(id)
            .pipe(
                take(1),
                catchError((e) => {
                    this.logger.apiError("Unfollow author failed", e);
                    return throwError(() => e);
                })
            )
            .subscribe();
    }

    public getAuthorVideos(
        id: number,
        page: number,
        pageSize: number,
        order: SortOrder | undefined,
        sort: VideoInfoSort | undefined,
        query: string | undefined
    ) {
        return this.authorsResource.getAuthorVideos(id, page, pageSize, order, sort, query).pipe(
            catchError((e) => {
                this.logger.apiError("Get author videos failed", e);
                return of({ count: 0, results: [] });
            })
        );
    }

    public publishForm(body: BodyPublishAuthorForm) {
        return this.authorsResource.publishAuthorForm(body).pipe(
            catchError((e) => {
                this.logger.apiError("Publish form failed", e);
                return throwError(() => e);
            })
        );
    }

    public createAuthor(body: ManageAuthorBody) {
        return firstValueFrom(
            this.authorsResource.createAuthor(body).pipe(
                catchError((e) => {
                    this.logger.apiError("Create author failed", e);
                    return throwError(() => e);
                })
            )
        );
    }

    public updateAuthor(id: number, body: ManageAuthorBody) {
        return firstValueFrom(
            this.authorsResource.updateAuthor(id, body).pipe(
                catchError((e) => {
                    this.logger.apiError("Update author failed", e);
                    return throwError(() => e);
                })
            )
        );
    }

    public deleteAuthor(id: number) {
        return firstValueFrom(
            this.authorsResource.deleteAuthor(id).pipe(
                catchError((e) => {
                    this.logger.apiError("Delete author failed", e);
                    return throwError(() => e);
                })
            )
        );
    }
}
